import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, useEffect } from 'react';
import {
  detectLineupConflicts,
  conflictFingerprint,
  type DancerFamilyRef,
  type LineupConflict,
  type LineupDanceWithCast,
  cascadeShowTimeline,
} from '@pirouette/shared';
import { useShows, useDances } from '@/data/productions';
import {
  useConflictOverrides,
  useAcceptConflict,
  useRevokeConflictOverride,
} from '@/data/conflicts';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/productions/$id/conflicts')({
  component: ConflictsTab,
});

interface AggregatedConflict {
  show_id: string;
  show_name: string;
  conflict: LineupConflict;
  fingerprint: string;
  override_id: string | null;
}

function useShowConflictsForProduction(productionId: string) {
  const shows = useShows(productionId);
  const dances = useDances(productionId);
  const overrides = useConflictOverrides(productionId);

  // Fetch every show's lineup + cast for this production. Two-step:
  // first all lineups, then cast for all dance ids encountered. We keep
  // each step a single round-trip rather than per-show fanout.
  const showIds = (shows.data ?? []).map((s) => s.id);
  const danceIds = (dances.data ?? []).map((d) => d.id);

  const lineupsQuery = useQuery({
    enabled: showIds.length > 0,
    queryKey: ['conflicts-lineups', productionId, [...showIds].sort().join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('show_lineups')
        .select('*')
        .in('show_id', showIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  const castQuery = useQuery({
    enabled: danceIds.length > 0,
    queryKey: ['conflicts-cast', productionId, [...danceIds].sort().join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cast_assignments')
        .select('dance_id, dancer_id, dancers(id, first_name, last_name, family_id)')
        .in('dance_id', danceIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  const aggregated = useMemo<AggregatedConflict[]>(() => {
    if (!shows.data || !dances.data) return [];
    const dancesById = new Map(dances.data.map((d) => [d.id, d]));
    const dancerFamily = new Map<string, DancerFamilyRef>();
    const castByDance = new Map<string, string[]>();
    for (const r of (castQuery.data ?? []) as {
      dance_id: string;
      dancer_id: string;
      dancers: { id: string; first_name: string; last_name: string; family_id: string } | null;
    }[]) {
      if (r.dancers) {
        dancerFamily.set(r.dancer_id, {
          dancer_id: r.dancer_id,
          family_id: r.dancers.family_id,
          display_name: `${r.dancers.first_name} ${r.dancers.last_name}`,
        });
      }
      const list = castByDance.get(r.dance_id) ?? [];
      list.push(r.dancer_id);
      castByDance.set(r.dance_id, list);
    }

    const lineupRows = lineupsQuery.data ?? [];
    const lineupsByShow = new Map<string, typeof lineupRows>();
    for (const l of lineupRows) {
      const list = lineupsByShow.get((l as { show_id: string }).show_id) ?? [];
      list.push(l);
      lineupsByShow.set((l as { show_id: string }).show_id, list);
    }

    const out: AggregatedConflict[] = [];
    for (const show of shows.data) {
      const showLineups = lineupsByShow.get(show.id) ?? [];
      const cascade = cascadeShowTimeline(
        {
          performance_at: show.performance_at,
          default_gap_seconds: show.default_gap_seconds,
          intermission_after_sequence: show.intermission_after_sequence,
          intermission_minutes: show.intermission_minutes,
        },
        showLineups
          .map((sl) => {
            const d = dancesById.get((sl as { dance_id: string }).dance_id);
            if (!d) return null;
            return {
              dance_id: d.id,
              sequence_index: (sl as { sequence_index: number }).sequence_index,
              length_seconds: d.length_seconds,
            };
          })
          .filter((x): x is NonNullable<typeof x> => x !== null),
      );
      const lineupWithCast: LineupDanceWithCast[] = cascade.map((c) => ({
        dance_id: c.dance_id,
        sequence_index: c.sequence_index,
        computed_start_time: c.computed_start_time,
        computed_end_time: c.computed_end_time,
        cast_dancer_ids: castByDance.get(c.dance_id) ?? [],
      }));
      const conflicts = detectLineupConflicts(lineupWithCast, dancerFamily);
      for (const c of conflicts) {
        out.push({
          show_id: show.id,
          show_name: show.name,
          conflict: c,
          fingerprint: '', // filled in below
          override_id: null,
        });
      }
    }
    return out;
  }, [shows.data, dances.data, lineupsQuery.data, castQuery.data]);

  // Compute fingerprints async + match against overrides.
  const [withFingerprints, setWithFingerprints] = useState<AggregatedConflict[]>([]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const overrideByFp = new Map((overrides.data ?? []).map((o) => [o.fingerprint, o]));
      // Parallel SHA-256 so a production with 50+ conflicts doesn't stall
      // the UI on a sequential await chain.
      const next = await Promise.all(
        aggregated.map(async (a) => {
          const fp = await conflictFingerprint(a.conflict);
          return {
            ...a,
            fingerprint: fp,
            override_id: overrideByFp.get(fp)?.id ?? null,
          };
        }),
      );
      if (!cancelled) setWithFingerprints(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [aggregated, overrides.data]);

  return {
    conflicts: withFingerprints,
    isLoading: shows.isLoading || dances.isLoading || lineupsQuery.isLoading || castQuery.isLoading,
  };
}

function ConflictsTab() {
  const { id: productionId } = Route.useParams();
  const { conflicts, isLoading } = useShowConflictsForProduction(productionId);
  const overrides = useConflictOverrides(productionId);
  const acceptConflict = useAcceptConflict();
  const revokeOverride = useRevokeConflictOverride();
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const active = conflicts.filter((c) => !c.override_id);
  const overridden = conflicts.filter((c) => c.override_id);

  async function acceptOne(fingerprint: string) {
    try {
      await acceptConflict.mutateAsync({
        production_id: productionId,
        fingerprint,
        reason: reason.trim(),
      });
      toast({ title: 'Conflict acknowledged', variant: 'success' });
      setOpenFor(null);
      setReason('');
    } catch (err) {
      toast({
        title: "Couldn't acknowledge",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function revoke(overrideId: string) {
    try {
      await revokeOverride.mutateAsync({ id: overrideId, production_id: productionId });
      toast({ title: 'Override removed', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't revoke",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-8">
      <header>
        <Link
          to="/admin/productions/$id"
          params={{ id: productionId }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Production
        </Link>
        <h1 className="mt-2">Conflicts</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Aggregated across every show in this production. Acknowledge a conflict to suppress the
          warning everywhere — useful when a parent is handling the quick-change backstage and you
          don't want the panel to keep nagging.
        </p>
      </header>

      {isLoading && <p aria-live="polite">Loading…</p>}

      {!isLoading && active.length === 0 && overridden.length === 0 && (
        <Empty
          title="No conflicts"
          description="Either the lineup is clean or no dancers have been cast yet."
        />
      )}

      {active.length > 0 && (
        <section aria-labelledby="active-conflicts">
          <h2 id="active-conflicts" className="text-[var(--text-xl)]">
            Active warnings
          </h2>
          <ul className="mt-4 grid gap-2">
            {active.map((c, i) => (
              <li
                key={`${c.show_id}-${c.fingerprint}-${i}`}
                className="surface flex flex-col gap-2 p-4"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <Badge
                      tone={
                        c.conflict.severity === 'high'
                          ? 'danger'
                          : c.conflict.severity === 'medium'
                            ? 'warn'
                            : 'info'
                      }
                    >
                      {c.conflict.kind}
                    </Badge>
                    <span className="ml-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      {c.show_name}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setOpenFor(openFor === c.fingerprint ? null : c.fingerprint)}
                  >
                    {openFor === c.fingerprint ? 'Cancel' : 'Acknowledge'}
                  </Button>
                </div>
                <p className="text-[var(--text-sm)]">{c.conflict.message}</p>
                {openFor === c.fingerprint && (
                  <div className="mt-1 grid gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3">
                    <Label htmlFor={`reason-${c.fingerprint}`}>Why is this OK?</Label>
                    <Input
                      id={`reason-${c.fingerprint}`}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. parent is handling the quick change backstage"
                    />
                    <Button
                      size="sm"
                      onClick={() => void acceptOne(c.fingerprint)}
                      disabled={!reason.trim() || acceptConflict.isPending || !c.fingerprint}
                    >
                      {acceptConflict.isPending ? 'Saving…' : 'Acknowledge'}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {overridden.length > 0 && (
        <section aria-labelledby="acknowledged-conflicts">
          <h2 id="acknowledged-conflicts" className="text-[var(--text-xl)]">
            Acknowledged
          </h2>
          <ul className="mt-4 grid gap-2">
            {overridden.map((c, i) => {
              const ov = (overrides.data ?? []).find((o) => o.id === c.override_id);
              return (
                <li
                  key={`ack-${c.show_id}-${c.fingerprint}-${i}`}
                  className="surface flex flex-wrap items-baseline justify-between gap-3 p-4"
                >
                  <div>
                    <Badge tone="success">{c.conflict.kind}</Badge>
                    <span className="ml-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      {c.show_name}
                    </span>
                    {ov && (
                      <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)] italic">
                        “{ov.reason}”
                      </p>
                    )}
                  </div>
                  {c.override_id && (
                    <Button size="sm" variant="ghost" onClick={() => void revoke(c.override_id!)}>
                      Revoke
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
